import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SEO from '../SEO/SEO';
import { motion } from 'framer-motion';
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { CheckCircle, User, Briefcase, DollarSign, Ruler, FileText, Globe, Star, Shield, Home, WrenchIcon, SettingsIcon, BadgeCheckIcon } from "lucide-react";
import {
  HomeIcon,
  CurrencyRupeeIcon,
  ClockIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  ChartBarIcon,
  BanknotesIcon,
  SparklesIcon,
  BuildingOffice2Icon,
  RocketLaunchIcon,
  StarIcon,
  CurrencyDollarIcon,
  CheckCircleIcon as CheckCircleSolid,
  BoltIcon,
  HeartIcon,
  LightBulbIcon,
  UserGroupIcon,
  KeyIcon,
  DocumentCheckIcon
} from '@heroicons/react/24/solid';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, UserIcon, GlobeAltIcon, BriefcaseIcon, Square2StackIcon, IdentificationIcon } from '@heroicons/react/24/outline';

const HomeRenovationLoan = () => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const [activeTab, setActiveTab] = useState('common');
  const [activeFAQ, setActiveFAQ] = useState(0);  // OPEN FIRST FAQ BY DEFAULT
  const [activeCalc, setActiveCalc] = useState("overview");

  // Fallback static banner data
  const fallbackBanner = {
    title: 'Upgrade Your Living Space with Aham Home Renovation Loan',
    subtitle: 'From painting to plumbing, get easy financing to modernize and enhance your home—your way.',
    image_url: 'img/loan-banners/Home-Renovation-Banner.webp',
    primary_button_text: 'Apply Now',
    primary_button_link: 'https://aham.mbwhost.in/contact',
    highlights: []
  };

  // Banner State
  const [banner, setBanner] = useState(null);
  const [bannerLoading, setBannerLoading] = useState(true);

  // Fetch Banner Data
  useEffect(() => {
    const fetchBanner = async () => {
      try {
        setBannerLoading(true);
        const response = await axios.get('/api/admin/banners/page/home_renovation_loan');
        if (response.data.success && response.data.data && response.data.data.length > 0) {
          setBanner(response.data.data[0]);
        } else {
          setBanner(fallbackBanner);
        }
      } catch (error) {
        console.error('Error fetching banner:', error);
        setBanner(fallbackBanner);
      } finally {
        setBannerLoading(false);
      }
    };

    fetchBanner();
  }, []);

  // Overview state
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        setOverviewLoading(true);
        const response = await axios.get('/api/admin/loan-overview/home_renovation_loan');
        const data = response.data.data;
        if (data) {
          // Parse features if it is a string
          const parsedData = {
            ...data,
            features: typeof data.features === 'string' ? JSON.parse(data.features) : data.features
          };
          setOverview(parsedData);
        }
      } catch (error) {
        console.error('Error fetching overview:', error);
      } finally {
        setOverviewLoading(false);
      }
    };
    fetchOverview();
  }, []);

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setCurrentLanguage(lang);
  };

  // Key Benefits Data
  // Icon Map for Dynamic Benefits
  const benefitIcons = {
    BanknotesIcon: BanknotesIcon,
    ClockIcon: ClockIcon,
    HomeIcon: HomeIcon,
    BuildingOffice2Icon: BuildingOffice2Icon,
    ShieldCheckIcon: ShieldCheckIcon,
    RocketLaunchIcon: RocketLaunchIcon,
    StarIcon: StarIcon,
    CurrencyDollarIcon: CurrencyDollarIcon,
    CheckCircleIcon: CheckCircleSolid,
    SparklesIcon: SparklesIcon,
    BoltIcon: BoltIcon,
    HeartIcon: HeartIcon,
    LightBulbIcon: LightBulbIcon,
    ChartBarIcon: ChartBarIcon,
    UserGroupIcon: UserGroupIcon,
    KeyIcon: KeyIcon,
    DocumentCheckIcon: DocumentCheckIcon
  };

  // Benefits State
  const [benefits, setBenefits] = useState([]);
  const [settings, setSettings] = useState({
    section_title: 'Key Benefits & Features',
    section_subtitle: 'Discover why our home construction loan is the right choice for building your dream home',
    cta_text: 'Request a Quote',
    cta_link: '/contact'
  });
  const [benefitsLoading, setBenefitsLoading] = useState(true);

  useEffect(() => {
    const fetchBenefits = async () => {
      try {
        setBenefitsLoading(true);
        const [benefitsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/key-benefits/home_renovation_loan/active'),
          axios.get('/api/admin/key-benefits/settings/home_renovation_loan')
        ]);

        if (benefitsRes.data.data) {
          setBenefits(benefitsRes.data.data);
        }
        if (settingsRes.data.data) {
          setSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching benefits:', error);
      } finally {
        setBenefitsLoading(false);
      }
    };
    fetchBenefits();
  }, []);

  // Eligibility States
  const [dynamicEligibility, setDynamicEligibility] = useState([]);
  const [eligibilitySettings, setEligibilitySettings] = useState({
    section_title: 'Eligibility Criteria',
    section_subtitle: "Find out if you're eligible for Aham Home Renovation Loan"
  });
  const [eligibilityLoading, setEligibilityLoading] = useState(true);

  // Fetch Eligibility Data
  useEffect(() => {
    const fetchEligibility = async () => {
      try {
        setEligibilityLoading(true);
        const [criteriaRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/eligibility/home_renovation_loan/active'),
          axios.get('/api/admin/eligibility/settings/home_renovation_loan')
        ]);

        if (criteriaRes.data.data) {
          setDynamicEligibility(criteriaRes.data.data);
        }
        if (settingsRes.data.data) {
          setEligibilitySettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching eligibility:', error);
      } finally {
        setEligibilityLoading(false);
      }
    };

    fetchEligibility();
  }, []);

  // Icon Map for Dynamic Elements
  const iconMap = {
    ...benefitIcons,
    UserIcon: User,
    GlobeAltIcon: Globe,
    BriefcaseIcon: Briefcase,
    DocumentTextIcon: FileText,
    Square2StackIcon: Ruler,
    IdentificationIcon: BadgeCheckIcon,
    HomeIcon: Home,
    ShieldCheckIcon: Shield,
    ChartBarIcon: ChartBarIcon,
    CheckCircle: CheckCircle
  };

  // Documents States
  const [dynamicDocuments, setDynamicDocuments] = useState({});
  const [documentsSettings, setDocumentsSettings] = useState({
    section_title: 'Documents Required',
    section_subtitle: 'Keep these documents ready for a smooth application process'
  });
  const [documentsLoading, setDocumentsLoading] = useState(true);

  // Fetch Documents Data
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setDocumentsLoading(true);
        const [documentsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/documents/home_renovation_loan/active'),
          axios.get('/api/admin/documents/settings/home_renovation_loan')
        ]);

        if (documentsRes.data.data) {
          // Group documents by category
          const grouped = {};
          documentsRes.data.data.forEach(doc => {
            if (!grouped[doc.category]) {
              grouped[doc.category] = [];
            }
            grouped[doc.category].push(doc.document_name);
          });
          setDynamicDocuments(grouped);
        }
        if (settingsRes.data.data) {
          setDocumentsSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        setDocumentsLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  // Interest Rates States
  const [interestCards, setInterestCards] = useState([]);
  const [interestSettings, setInterestSettings] = useState({
    section_title: 'Affordable Home Loan',
    section_subtitle: 'Interest Rates',
    intro_text: 'Aham Housing Finance brings you transparent, competitive, and customer-friendly interest rates designed to make home renovation affordable and stress-free.',
    closing_text: 'Begin your home renovation journey with confidence and clarity. Aham ensures the process is smooth, transparent, and tailored to your needs.',
    cta_text: 'Calculate EMI',
    cta_link: 'https://aham.mbwhost.in/emi-calculator'
  });
  const [interestLoading, setInterestLoading] = useState(true);

  // Fetch Interest Rates Data
  useEffect(() => {
    const fetchInterestData = async () => {
      try {
        setInterestLoading(true);
        const [cardsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/interest-rates/home_renovation_loan/active'),
          axios.get('/api/admin/interest-rates/settings/home_renovation_loan')
        ]);

        if (cardsRes.data.data) {
          setInterestCards(cardsRes.data.data);
        }
        if (settingsRes.data.data) {
          setInterestSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching interest rates data:', error);
      } finally {
        setInterestLoading(false);
      }
    };

    fetchInterestData();
  }, []);

  // FAQs States
  const [dynamicFAQs, setDynamicFAQs] = useState([]);
  const [faqSettings, setFaqSettings] = useState({
    section_title: 'Frequently Asked Questions',
    section_subtitle: 'Find answers to common questions about Home Renovation Loan'
  });
  const [faqsLoading, setFaqsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // CTA Banner States
  const [ctaBannerSettings, setCtaBannerSettings] = useState({
    title: 'Ready to Renovate Your Home?',
    description: "Take the first step towards improving your living space with Aham's hassle-free loan process",
    button_text: 'Talk to Our Loan Expert',
    button_link: 'https://aham.mbwhost.in/contact',
    background_image: 'img/renovation-loan/home-renovation-loan.webp'
  });

  // Fetch FAQs and CTA Banner Data
  // Fetch FAQs and CTA Banner Data
  // Fetch FAQs and CTA Banner Data
  useEffect(() => {
    const fetchFAQsAndCTA = async () => {
      try {
        setFaqsLoading(true);
        setFetchError(null);
        console.log('Fetching FAQs and CTA...');
        const [faqsRes, settingsRes, ctaRes] = await Promise.all([
          axios.get('/api/admin/faqs/home_renovation_loan/active'),
          axios.get('/api/admin/faqs/settings/home_renovation_loan'),
          axios.get('/api/admin/cta-banner/home_renovation_loan')
        ]);

        console.log('FAQs Response:', faqsRes.data);

        if (faqsRes.data.data && Array.isArray(faqsRes.data.data)) {
          console.log('FAQs loaded:', faqsRes.data.data.length);
          setDynamicFAQs(faqsRes.data.data);
        } else {
          console.warn('No FAQS found in response or invalid format');
          setDynamicFAQs([]);
        }

        if (settingsRes.data.data) {
          setFaqSettings(settingsRes.data.data);
        }
        if (ctaRes.data.data) {
          setCtaBannerSettings(ctaRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching FAQs/CTA data:', error);
        setFetchError(error.message + (error.response ? ` (${error.response.status})` : ''));
        setDynamicFAQs([]);
      } finally {
        setFaqsLoading(false);
      }
    };

    fetchFAQsAndCTA();
  }, []);


  // Eligibility Criteria
  const eligibility = [
    "Applicant must be the owner of the property being renovated",
    "Available for Salaried, Self-Employed, and Business Owners",
    "Income and credit evaluation as per standard Aham norms",
    "Age requirement: Minimum 21 years at loan origination",
    "Maximum age at loan maturity: 60 years (salaried) / 65 years (self-employed)",
    "Property must have clear title and be located within serviceable limits"
  ];

  // Matching icons & colors for each item
  const icons = [
    { icon: <User />, from: "from-blue-500", to: "to-cyan-400" },
    { icon: <Globe />, from: "from-green-500", to: "to-lime-400" },
    { icon: <Briefcase />, from: "from-pink-500", to: "to-rose-400" },
    { icon: <DollarSign />, from: "from-orange-500", to: "to-amber-400" },
    { icon: <FileText />, from: "from-purple-500", to: "to-indigo-400" },
    { icon: <Shield />, from: "from-teal-500", to: "to-emerald-400" },
    { icon: <Star />, from: "from-yellow-500", to: "to-orange-400" },
    { icon: <Ruler />, from: "from-red-500", to: "to-orange-400" },
  ];

  // Documents Required
  const documents = {
    common: [
      "Completed loan application form with passport-size photographs",
      "Identity Proof – Aadhaar, PAN, Passport, Driving License, Voter ID",
      "Address Proof – Aadhaar, Passport, Utility Bills, Rent Agreement",
      "Bank statements (Last 6 months)"
    ],

    salaried: [
      "Last 3 months salary slips",
      "6 months bank statements with salary credits",
      "Form 16 or Income Tax Returns (last 2 years)",
      'Employee ID Card'
    ],

    selfEmployed: [
      "Business registration proof – GST, TIN, Trade License",
      "Audited financial statements or ITR for last 2–3 years",
      "Business bank account statements (last 12 months)",
      'GST Returns (if applicable)',
    ],

    nri: [
      "Valid passport and visa",
      "Overseas employment contract and income proof",
      "NRE/NRO bank account statements (6 months)",
      "Power of Attorney if applicable"
    ],

    property: [
      "Property ownership documents / Title deed",
      "Previous sale deed and tax receipts",
      "Renovation estimate / work order / contractor quotation",
      "Photographs of existing condition (if requested)"
    ]
  };





  const toggleFAQ = (index) => {
    setActiveFAQ(activeFAQ === index ? null : index);
  };


  // Scroll Event Listener to auto-switch active tab
  useEffect(() => {
    const handleScroll = () => {
      const sections = ["overview", "benefits", "eligibility", "documents", "interest", "faq"];

      sections.forEach((sec) => {
        const element = document.getElementById(sec);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveCalc(sec);
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (!element) return;

    const headerHeight = 120; // top navbar + sticky navbar height

    const elementPosition =
      element.getBoundingClientRect().top + window.pageYOffset;

    const offsetPosition = elementPosition - headerHeight;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };
            
            
            
  // Schema for SEO
  const renovationSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://ahamhfc.com/#organization",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "legalName": "AHAM HOUSING FINANCE LIMITED",
        "alternateName": "Aham Housing Finance Private Limited",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "image": "https://ahamhfc.com/img/loan-banners/Slide-02-1770722226693-126687268.webp?t=1770802805000",
        "description": "AHAM HOUSING FINANCE LIMITED offers a wide range of home loans across various segments and diverse regions in India.",
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com",
        "sameAs": [
          "https://www.facebook.com/ahamhfc",
          "https://www.instagram.com/aham_housingfinance/",
          "https://linkedin.com/company/ahamhfc",
          "https://www.youtube.com/@ahamhfc"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://ahamhfc.com/#website",
        "url": "https://ahamhfc.com/",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "inLanguage": "en-IN"
      },
      {
        "@type": "FinancialService",
        "@id": "https://ahamhfc.com/#financialservice",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "description": "AHAM HOUSING FINANCE LIMITED offers home loans and affordable housing finance solutions across multiple customer segments in India.",
        "areaServed": {
          "@type": "Country",
          "name": "India"
        },
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com"
      },
      {
        "@type": "WebPage",
        "@id": "https://ahamhfc.com/home-renovation-loan/#webpage",
        "url": "https://ahamhfc.com/home-renovation-loan",
        "name": "Home Renovation Loan - Aham Housing Finance",
        "description": "Upgrade your living space with a home renovation loan from Aham Housing Finance and explore transparent, competitive, and customer-friendly rates.",
        "isPartOf": {
          "@id": "https://ahamhfc.com/#website"
        },
        "about": {
          "@id": "https://ahamhfc.com/home-renovation-loan/#loan"
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "@id": "https://ahamhfc.com/home-renovation-loan/#primaryimage",
          "contentUrl": "https://ahamhfc.com/img/loan-overview/Home-Renovation-Overview-1770902949624-840692171.webp"
        },
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        }
      },
      {
        "@type": "LoanOrCredit",
        "@id": "https://ahamhfc.com/home-renovation-loan/#loan",
        "name": "Home Renovation Loan",
        "url": "https://ahamhfc.com/home-renovation-loan",
        "image": {
          "@id": "https://ahamhfc.com/home-renovation-loan/#primaryimage"
        },
        "description": "Specialized renovation finance to upgrade, renovate, and modernize your home with transparent, competitive, and customer-friendly rates.",
        "category": "Housing Finance",
        "loanType": "Home Renovation Loan",
        "provider": {
          "@id": "https://ahamhfc.com/#organization"
        },
         "audience": {
          "@type": "Audience",
          "audienceType": "Homeowners planning renovation or modernization"
        },
        "areaServed": {
          "@type": "Country",
          "name": "India"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://ahamhfc.com/home-renovation-loan/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://ahamhfc.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Home Renovation Loan",
            "item": "https://ahamhfc.com/home-renovation-loan"
          }
        ]
      }
    ]
  };



  return (
    <div className="min-h-screen bg-white">

      <SEO
        title="Home Renovation Loan: Attractive Rates | Aham Housing Finance"
        description="Renovate your home with a Home Renovation Loan from Aham Housing Finance. Enjoy attractive rates and easy EMIs. Apply now!"
        keywords="Aham home renovation loan, Aham Housing Finance home renovation loan, home renovation loan, home improvement loan, house renovation loan, loan for home renovation, home makeover loan, repair and renovation loan, housing renovation finance, affordable home renovation loan, home renovation loan India, flexible EMI renovation loan, tenure up to 20 years renovation loan, low documentation renovation loan, no income proof renovation loan, self employed home renovation loan, home upgrade loan, kitchen remodeling loan, bathroom renovation loan, Aham Housing Finance renovation loan"
        canonical="https://ahamhfc.com/home-renovation-loan"
        ogTitle="Home Renovation Loan: Attractive Rates | Aham Housing Finance"
        ogDescription="Apply for a home renovation loan with attractive interest rates & easy EMIs. Modernize your home with tax benefits up to ₹30,000. Check eligibility with Aham today!"
        schema={renovationSchema}
           />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-start overflow-hidden">
        {bannerLoading ? (
          // Loading state
          <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
            <div className="text-white text-center">
              <div className="animate-pulse">
                <div className="h-8 w-48 bg-white/20 rounded mx-auto mb-4"></div>
                <div className="h-4 w-64 bg-white/10 rounded mx-auto"></div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url(${banner?.image_url || 'img/loan-banners/Home-Renovation-Banner.webp'})`,
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
            </div>

            <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left w-full">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-3xl"
              >
             

                <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 leading-tight">
                  {banner?.title || "Upgrade Your Living Space with Aham Home Renovation Loan"}
                </h1>
                <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed font-medium">
                  {banner?.subtitle || "From painting to plumbing, get easy financing to modernize and enhance your home—your way."}
                </p>

                {/* Display highlights if available */}
                {banner?.highlights && banner.highlights.length > 0 && (
                  <div className="flex flex-wrap gap-4 mb-8">
                    {banner.highlights.map((highlight, index) => (
                      <div key={index} className="flex items-center space-x-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 shadow-lg">
                        <CheckCircleIcon className="h-4 w-4 text-yellow-400" />
                        <span className="text-white text-xs md:text-sm font-medium">{highlight}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  {banner?.primary_button_text && (
                    <a href={banner?.primary_button_link || 'https://aham.mbwhost.in/contact'} className="w-full sm:w-auto text-left">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-white text-primary-600 hover:bg-primary-50 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto md:mx-0"
                      >
                        {banner?.primary_button_text}
                      </motion.button>
                    </a>
                  )}

                  {banner?.secondary_button_text && (
                    <a href={banner?.secondary_button_link || '#'} className="w-full sm:w-auto text-left">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-primary-600 text-white hover:bg-primary-700 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto md:mx-0 border border-white/20"
                      >
                        {banner?.secondary_button_text}
                      </motion.button>
                    </a>
                  )}
                </div>
                
                   {/* Breadcrumb Navigation */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                  className="flex items-center space-x-2 mb-6"
                >
                  <Link
                    to="/"
                    className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
                  >
                    Home
                  </Link>
                  <ChevronRightIcon className="h-4 w-4 text-white/60" />
                  <span className="text-yellow-300 text-sm font-semibold">Home Renovation Loan</span>
                </motion.div>
                
              </motion.div>
            </div>
          </>
        )}
      </section>


      {/* Sticky Finance Navigation */}
      <div className="hidden md:block w-full py-3 sticky top-[80px] z-50">
        <div className="container mx-auto px-1">

          <div className="w-full bg-white rounded-full shadow-lg flex flex-wrap justify-center gap-2 md:gap-6 py-3 px-4">

            {[
              { id: "overview", label: "Overview" },
              { id: "benefits", label: "Benefits" },
              { id: "eligibility", label: "Loan Eligibility Criteria" },
              { id: "documents", label: "Documents" },
              { id: "interest", label: "Interest" },
              { id: "faq", label: "FAQ" }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveCalc(tab.id);
                  scrollToSection(tab.id); // ONLY this — smooth scroll with offset
                }}
                className={`
    px-6 py-2 rounded-full font-semibold transition-all 
    ${activeCalc === tab.id
                    ? "bg-yellow-400 text-blue-800 shadow-lg scale-105"
                    : "text-gray-500 hover:text-blue-700"
                  }
  `}
              >
                {tab.label}
              </button>

            ))}
          </div>
        </div>
      </div>


      {/* Overview Section */}
     <section id="overview" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center md:text-left">
                {overview?.title || "Home Renovation Loan"}
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                {overview?.description ? (
                  <div dangerouslySetInnerHTML={{ __html: overview.description }} />
                ) : (
                  <p>
                    Your home deserves care, upgrades, and improvements that keep it beautiful, functional, and safe. Aham Housing Finance offers a dedicated
                    <strong> Home Renovation Loan </strong>
                    to help you improve your existing self-owned residential property—without the need to increase its living space.
                  </p>
                )}

                {overview?.features && Array.isArray(overview.features) && overview.features.length > 0 ? (
                  <ul className="space-y-3 mt-6">
                    {overview.features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <CheckCircleIcon className="h-6 w-6 text-primary-600 mt-1 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  // Fallback static features if dynamic ones fail specifically
                  <ul className="space-y-3 mt-6">
                    <li className="flex items-start space-x-3">
                      <CheckCircleIcon className="h-6 w-6 text-primary-600 mt-1" />
                      <span><strong>Loan from ₹5 lakhs to ₹15 lakhs</strong> – Perfect for small to medium scale renovation projects.</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircleIcon className="h-6 w-6 text-primary-600 mt-1" />
                      <span><strong>Tenure up to 20 years</strong> – Flexible EMIs to match your financial comfort.</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircleIcon className="h-6 w-6 text-primary-600 mt-1" />
                      <span><strong>Maximum LTV of 60%</strong> – Funding based on property value and renovation scope.</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircleIcon className="h-6 w-6 text-primary-600 mt-1" />
                      <span><strong>Covers internal & external repairs</strong> – Improve your home without expanding built-up area.</span>
                    </li>
                  </ul>
                )}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={overview?.image_url || "img/loan-overview/Home-Renovation-Overview.webp"}
                alt="Aham Housing Finance expert discussing home renovation loan benefits and attractive interest rates with a homeowner"
                className="rounded-2xl shadow-large w-full object-cover h-[500px]"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "img/loan-overview/Home-Renovation-Overview.webp";
                }}
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl max-w-xs hidden md:block">
                <div className="flex items-center gap-4 mb-2">
                  <div className="p-3 bg-yellow-100 rounded-lg">
                    <SparklesIcon className="h-8 w-8 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Transform</h3>
                    <p className="text-sm text-gray-600">Your Living Space</p>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>






      {/* Key Benefits Section */}
      < section id="benefits" className="relative py-24 overflow-hidden" >

        {/* 🔥 Background – gradient + subtle pattern */}
        < div
          className="absolute inset-0 bg-cover bg-center opacity-1"
          style={{
            backgroundImage:
              "url('/img/construction-loan/Key-Benefits.webp')",
          }}
        ></div >

        {/* Dark gradient overlay */}
        < div className="absolute inset-0 bg-gradient-to-b from-black/100 via-white/70 to-white" ></div >

        <div className="relative container mx-auto px-4">
          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16 px-4"
          >
            <h2 className="text-4xl md:text-5xl font-extrabold text-white drop-shadow-lg mb-6 leading-tight">
              {settings.section_title}
            </h2>
            <p className="text-lg md:text-xl text-white mt-4 max-w-3xl mx-auto leading-relaxed font-medium">
              {settings.section_subtitle}
            </p>
          </motion.div>

          {/* Benefit Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {benefits.map((benefit, index) => {
              const IconComponent = benefitIcons[benefit.icon_name] || StarIcon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -12, scale: 1.03 }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  viewport={{ once: true }}
                  className="relative bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col items-center text-center"
                >
                  {/* Floating Glow */}
                  <div className={`absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-r from-${benefit.gradient_from || 'blue-500'} to-${benefit.gradient_to || 'indigo-600'} rounded-full blur-3xl opacity-20`}></div>

                  {/* Icon */}
                  <div className={`bg-gradient-to-br from-${benefit.gradient_from || 'primary-600'} to-${benefit.gradient_to || 'primary-400'} w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg`}>
                    <IconComponent className="h-9 w-9 text-white" />
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {benefit.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-700 leading-relaxed font-medium">
                    {benefit.description}
                  </p>
                </motion.div>
              );
            })}
          </div>

          {/* Button */}
          <div className="mt-16 flex justify-center">
            <a href={settings.cta_link || "/contact"} className="w-full flex justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] hover:bg-primary-700 text-white px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto mx-auto"
              >
                {settings.cta_text || "Request a Quote"}
              </motion.button>
            </a>
          </div>
        </div>
      </section >

      {/* Eligibility Criteria Section */}
      < section id="eligibility" className="py-24 bg-gradient-to-b from-white to-primary-50 relative overflow-hidden" >

        {/* Soft Background Blobs */}
        < div className="absolute top-10 left-10 w-72 h-72 bg-primary-200/30 blur-3xl rounded-full" ></div >
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary-300/20 blur-3xl rounded-full"></div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-16">

            {/* Tag */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-block px-5 py-2 bg-primary-100 text-primary-700 rounded-full font-medium shadow-sm mb-6"
            >
              Loan Eligibility
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-extrabold text-gray-900 mt-6 mb-4 leading-tight tracking-tight"
            >
              {eligibilitySettings.section_title}
            </motion.h2>

            <p className="text-lg md:text-xl text-gray-600 mt-4 max-w-2xl mx-auto leading-relaxed">
              {eligibilitySettings.section_subtitle}
            </p>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {dynamicEligibility.map((item, index) => {
              const IconComp = iconMap[item.icon_name] || Star;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="
                    group backdrop-blur-xl bg-white/70 
                    border border-white/40 shadow-xl rounded-2xl 
                    p-6 flex flex-col items-center text-center gap-5 
                    hover:-translate-y-2 hover:shadow-2xl 
                    transition-all duration-300
                  "
                >
                  {/* Gradient Icon */}
                  <div
                    className={`
                    w-14 h-14 rounded-xl flex items-center justify-center 
                    bg-gradient-to-br from-${item.gradient_from || 'blue-500'} to-${item.gradient_to || 'blue-600'}
                    text-white shadow-lg group-hover:scale-105 transition
                  `}
                  >
                    <IconComp className="h-8 w-8" />
                  </div>

                  <div className="flex-1">
                    <p className="text-gray-800 font-semibold text-lg leading-snug">
                      {item.criteria}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* CTA Button */}
          {/* <div className="text-center mt-16">
            <a href="/customer-360-eligibility-calculator">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="
              px-10 py-4 rounded-full text-white font-semibold 
             bg-[#ed2636]
              shadow-lg hover:bg-primary-600 transition
            "
              >
                Check Your Eligibility Now
              </motion.button>
            </a>
          </div> */}

        </div>
      </section >

      {/* Documents Required Section */}
      < section id="documents" className="py-24 bg-gradient-to-b from-white to-primary-50 relative overflow-hidden" >

        {/* Background blur circles */}
        < div className="absolute top-0 left-0 w-96 h-96 bg-primary-200/20 blur-3xl rounded-full" ></div >
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-primary-300/20 blur-3xl rounded-full"></div>

        <div className="container mx-auto px-6 relative z-10">

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-extrabold text-gray-900 mb-4">
              {documentsSettings.section_title}
            </h2>
            <p className="text-xl text-gray-600">
              {documentsSettings.section_subtitle}
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto">

            {/* Premium Tabs */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {[
                { key: "common", label: "Common", icon: <FileText /> },
                { key: "salaried", label: "Salaried", icon: <Briefcase /> },
                { key: "selfEmployed", label: "Self-Employed", icon: <User /> },
                { key: "nri", label: "NRI", icon: <Globe /> },
                { key: "property", label: "Property", icon: <Home /> },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`
              flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 backdrop-blur-xl
              border border-white/40 shadow-lg
              ${activeTab === tab.key
                      ? "bg-gradient-to-r from-primary-600 to-primary-500 text-white scale-105 shadow-primary/50"
                      : "bg-white/70 text-gray-700 hover:bg-white/90"
                    }
            `}
                >
                  <span className="text-lg">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Content Card */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="backdrop-blur-xl bg-white/70 p-10 rounded-3xl border border-white/40 shadow-xl"
            >
              <motion.ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(dynamicDocuments[activeTab] || documents[activeTab]).map((doc, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.4 }}
                    className="
                flex items-start gap-4 
                p-5 bg-gradient-to-br from-white to-gray-50 
                rounded-2xl shadow-lg hover:shadow-2xl transition-all border border-gray-200
              "
                  >
                    <div className="w-12 h-12 bg-primary-100 text-primary-600 rounded-xl flex items-center justify-center shadow-inner">
                      <FileText className="h-6 w-6" />
                    </div>
                    <span className="text-gray-900 font-semibold">{doc}</span>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>

          </div>
        </div>
      </section >


      {/* Interest Rates Section */}
      <section
        id="interest"
        className="relative py-24 bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 text-white overflow-hidden"
      >
        {/* Decorative Blurs */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-white/10 rounded-full blur-3xl opacity-40"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary-400/20 rounded-full blur-3xl opacity-30"></div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto text-center"
          >
            {/* Title */}
            <h2 className="text-5xl md:text-6xl font-extrabold mb-8 leading-tight drop-shadow-xl">
              {interestSettings.section_title}
              <span className="block text-primary-200 mt-2">{interestSettings.section_subtitle}</span>
            </h2>

            {/* Description Section */}
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-10 md:p-14 shadow-2xl">
              <p className="text-xl text-white/90 leading-relaxed mb-6">
                {interestSettings.intro_text}
              </p>

              <div className="grid md:grid-cols-3 gap-8 my-10">
                {interestCards.map((card, index) => (
                  <motion.div
                    key={card.id || index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 + index * 0.2 }}
                    className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur-lg shadow-lg flex flex-col items-center text-center"
                  >
                    <div className="text-3xl mb-3">{card.icon}</div>
                    <h4 className="text-xl font-semibold mb-2">{card.title}</h4>
                    <p className="text-white/80 text-sm leading-relaxed">
                      {card.description}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* Final Message */}
              <p className="text-lg text-white/85 mb-10 leading-relaxed">
                {interestSettings.closing_text}
              </p>

              {/* CTA Button */}
              {/* <motion.a
                href={interestSettings.cta_link}
                whileHover={{ scale: 1.07 }}
                whileTap={{ scale: 0.95 }}
                className="inline-block bg-[#ed2636] text-white hover:text-black hover:bg-primary-50 px-10 py-4 rounded-xl font-semibold text-xl transition-all duration-300 shadow-xl"
              >
                {interestSettings.cta_text}
              </motion.a> */}
            </div>
          </motion.div>
        </div>
      </section>


      {/* FAQ Section */}
      < section id="faq" className="py-20 bg-white" >
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {faqSettings.section_title}
            </h2>
            <p className="text-xl text-gray-600">
              {faqSettings.section_subtitle}
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {faqsLoading ? (
              <div className="text-center py-10">
                <p className="text-gray-500">Loading FAQs...</p>
              </div>
            ) : dynamicFAQs.length > 0 ? (
              dynamicFAQs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="mb-4"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className={`w-full text-left p-6 rounded-xl transition-all duration-300 ${activeFAQ === index
                      ? 'bg-primary-50 border-2 border-primary-600'
                      : 'bg-gray-50 border-2 border-transparent hover:border-gray-300'
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 pr-4">
                        {faq.question}
                      </h3>
                      {activeFAQ === index ? (
                        <ChevronUpIcon className="h-6 w-6 text-primary-600 flex-shrink-0" />
                      ) : (
                        <ChevronDownIcon className="h-6 w-6 text-gray-400 flex-shrink-0" />
                      )}
                    </div>
                    {activeFAQ === index && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-4 text-gray-700 leading-relaxed"
                      >
                        {faq.answer}
                      </motion.p>
                    )}
                  </button>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-10">
                <p className="text-gray-500 mb-2">No FAQs currently available.</p>
                {fetchError && (
                  <div className="text-red-500 text-sm bg-red-50 p-2 rounded inline-block border border-red-200">
                    Error: {fetchError}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section >

      {/* CTA Banner */}
      < section className="py-20 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 relative overflow-hidden" >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url(${ctaBannerSettings.background_image || 'img/renovation-loan/home-renovation-loan.webp'})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center text-white"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {ctaBannerSettings.title}
            </h2>
            <p className="text-xl mb-10 max-w-2xl mx-auto text-white/90">
              {ctaBannerSettings.description}
            </p>
            <div className="flex justify-center">
              <motion.a
                href={ctaBannerSettings.button_link}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] text-white hover:bg-white hover:text-primary-600 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto mx-auto"
              >
                {ctaBannerSettings.button_text}
              </motion.a>
            </div>

          </motion.div>
        </div>
      </section >


    </div >
  );
};

export default HomeRenovationLoan;